'use client';

import { Suspense, useCallback, useMemo } from 'react';
import {
  useSelectedVariant,
  useSelectionActions,
  useBundleSelection
} from '@/components/product/product-context';
import FinanceMsg from '@/components/FinanceMsg';
import { useCampaign } from '@/app/CampaignContext';
import Price from '@/components/price/Price';
import OptionDropdown, {
  DropdownSkeleton
} from '@/components/selectors/OptionDropdown';
import {
  PriceSkeleton,
  FinanceMsgSkeleton
} from '@/components/price/PriceSkeleton';

const SIZE_SELECTOR_CLASS =
  'body-r text-grey-text focus-visible:ring-blue mt-2 block w-full cursor-pointer truncate bg-white py-3.5 pr-8 pl-5 focus-visible:border focus-visible:border-black focus-visible:ring-4';

/**
 * This component is meant to be used only with bundles
 */
export default function PriceAndSelector({ block, position }) {
  const { selector_label, tag_text, _metadata } = block.price_and_selector;
  const campaign = useCampaign();

  return (
    <section
      id={`${_metadata.uid}-${position}`}
      data-name="price-and-selector"
      className={`bg-parlor-green-95 p-4 not-last:mb-8`}
    >
      <span className="badge-text-r border-light-green text-light-green mb-2 inline-block self-center rounded-full border px-4 py-1 leading-tight!">
        {tag_text}
      </span>
      <div className="min-h-7.5">
        <Suspense
          fallback={
            <PriceSkeleton showPriceDetails={!!campaign.pricing_details} />
          }
        >
          <BundlePrice pricingDetailsData={campaign.pricing_details} />
        </Suspense>
      </div>
      <div className="body-s mt-3 min-h-5.25 text-white">
        <Suspense fallback={<FinanceMsgSkeleton />}>
          <BundleFinanceMsg />
        </Suspense>
      </div>
      <div className="mt-8">
        <label htmlFor="bundle-size-selector" className="sh12 mb-2 text-white">
          {selector_label}
        </label>
        <div className="text-crisp-white relative w-full cursor-pointer">
          <div className="relative mt-2 mb-6 min-h-13.5">
            <Suspense
              fallback={<DropdownSkeleton className={SIZE_SELECTOR_CLASS} />}
            >
              <BundleSizeSelector />
            </Suspense>
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * Reads bundle selection state, so it needs to be wrapped in a Suspense tag.
 */
function BundlePrice({ pricingDetailsData }) {
  const bundleState = useBundleSelection();
  const priceSummary = bundleState?.priceSummary;

  if (!priceSummary?.price) {
    return null;
  }

  return (
    <Price
      price={priceSummary.price}
      compareAtPrice={priceSummary.compareAtPrice}
      priceClassOverride="price-l"
      crossedPriceClassOverride="price-l-crossed text-dawn-grey-60!"
      showSavings={true}
      darkPdp={true}
      pricingDetailsData={pricingDetailsData}
      pricingDetailsClass={
        'inline-block text-dawn-grey-30 body-xs text-right pdp-price-details w-full self-center'
      }
      variant="price-selector-bundle"
    />
  );
}

/**
 * Reads bundle selection state, so it needs to be wrapped in a Suspense tag.
 */
function BundleFinanceMsg() {
  const bundleState = useBundleSelection();
  const price = bundleState?.priceSummary?.price;

  if (!price) {
    return null;
  }

  return <FinanceMsg isBundle={true} bundlePrice={price} />;
}

/**
 * Reads selected option state and bundle selection state, so it needs to be
 * wrapped in a Suspense tag.
 */
function BundleSizeSelector() {
  const { selectedOptions } = useSelectedVariant();
  const { updateOption } = useSelectionActions();
  const bundleState = useBundleSelection();
  const mainVariants = bundleState?.details?.pricingMetafield?.variants;
  const bundleSettings = bundleState?.details?.bundleSettings;
  const currentOffers = bundleState?.currentOffers;
  const optionNameLowercased =
    mainVariants?.[0]?.options?.[0]?.name?.toLowerCase();
  const selectedOption = optionNameLowercased
    ? selectedOptions?.[optionNameLowercased]
    : undefined;

  const checkBundleOutOfStock = useCallback(
    (variantValue) => {
      const bundleOutOfStock = bundleSettings?.outOfStock?.value === 'true';

      if (bundleOutOfStock) return true;

      if (!currentOffers) return false;

      return currentOffers.some((offer) => {
        if (offer.product?.outOfStock?.value === 'true') {
          return true;
        }

        if (offer.product?.variants) {
          const matchingVariant = offer.product.variants.find((variant) => {
            if (variant.title === variantValue) return true;

            if (variant.title.includes(variantValue)) return true;

            return variant.selectedOptions?.some(
              (option) => option.value === variantValue
            );
          });

          if (
            matchingVariant &&
            (matchingVariant.outOfStock?.value === 'true' ||
              matchingVariant.outOfStock?.value === true)
          ) {
            return true;
          }
        }

        const outOfStockMetafield = offer.product?.metafields?.find(
          (metafield) => metafield?.key === 'product_out_of_stock'
        );

        return outOfStockMetafield?.value?.toLowerCase() === 'true';
      });
    },
    [currentOffers, bundleSettings]
  );

  const dropdownOptions = useMemo(() => {
    if (!mainVariants?.length) {
      return [];
    }

    return mainVariants.map((variant) => {
      const value = variant.name;

      if (checkBundleOutOfStock(value)) {
        return {
          value,
          label: `${value} (Out of Stock)`
        };
      }

      return {
        value,
        label: value
      };
    });
  }, [mainVariants, checkBundleOutOfStock]);

  const handleChange = useCallback(
    (e) => {
      if (!optionNameLowercased) {
        return;
      }

      updateOption({ [optionNameLowercased]: e.target.value });
    },
    [optionNameLowercased, updateOption]
  );

  if (!mainVariants?.length) {
    return null;
  }

  return (
    <OptionDropdown
      id="bundle-size-selector"
      name="bundle-size-selector"
      options={dropdownOptions}
      value={selectedOption}
      onChange={handleChange}
      className={SIZE_SELECTOR_CLASS}
    />
  );
}
